import { queryOptions } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';

import { QualityApiError, responseError } from './quality.js';

const API_BASE = '/v1/quality';

/** A test management system whose projects Quality can list for a source link. */
export type TmsProvider = 'testrail' | 'zephyr' | 'xray' | 'azure';

/** One project of a test management system, as a source link names it. */
export interface TmsProjectRef {
  externalProjectId: string;
  name: string;
}

/** A page of TMS projects. `truncated` says the system had more than Quality returned. */
export interface TmsProjectList {
  projects: TmsProjectRef[];
  truncated: boolean;
}

/** Which credential store answered for a connector: the workspace connector, or the project's own account. */
export type ConnectorStatusSource = 'identity' | 'project' | null;

/** Whether Quality can use one connector in this project, and which store says so. */
export interface QualityConnectorStatus {
  provider: string;
  connected: boolean;
  source: ConnectorStatusSource;
  /** The workspace owner whose connection the caller chose; absent from an older service. */
  chosenOwnerId?: string | null;
  /** Owners whose connection the caller may choose, sent only while the caller has none of their own. */
  ownerOptions?: string[];
}

/** Quality's merged view of the project's connectors. `unavailable` means identity did not answer. */
export interface QualityConnectorStatusList {
  connectors: QualityConnectorStatus[];
  unavailable: boolean;
}

interface TmsProjectsWire {
  projects?: { external_project_id?: unknown; name?: unknown }[];
  truncated?: unknown;
}

interface ConnectorStatusWire {
  connectors?: {
    provider?: unknown;
    connected?: unknown;
    source?: unknown;
    chosen_owner_id?: unknown;
    owner_options?: unknown;
  }[];
  unavailable?: unknown;
}

/**
 * Reads a Quality setup endpoint. A 404 or a 403 answers null: a 404 means the route belongs to a
 * newer quality-service that is not deployed yet, and a 403 is what a viewer gets by design. Either
 * way the caller falls back to what it showed before.
 */
async function getOptionalJson<T>(
  path: string,
  fallback: string,
): Promise<T | null> {
  const res = await apiFetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
  });
  if (res.status === 404 || res.status === 403) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new QualityApiError(res.status, text.trim() || fallback);
  }
  return (await res.json()) as T;
}

/** Maps one listed project off the wire, dropping a row without an id. */
function tmsProjectFromWire(row: {
  external_project_id?: unknown;
  name?: unknown;
}): TmsProjectRef | null {
  const id = row.external_project_id;
  if (typeof id !== 'string' && typeof id !== 'number') return null;
  const externalProjectId = String(id);
  return {
    externalProjectId,
    name: typeof row.name === 'string' ? row.name : externalProjectId,
  };
}

/** The projects the linked TMS account can see, or null where the service has no such route yet. */
export async function fetchTmsProjects(
  projectId: string,
  provider: TmsProvider,
): Promise<TmsProjectList | null> {
  const wire = await getOptionalJson<TmsProjectsWire>(
    `/projects/${encodeURIComponent(projectId)}/tms-projects?provider=${encodeURIComponent(provider)}`,
    'Failed to load projects',
  );
  if (!wire) return null;
  return {
    projects: (wire.projects ?? [])
      .map(tmsProjectFromWire)
      .filter((project): project is TmsProjectRef => project !== null),
    truncated: wire.truncated === true,
  };
}

/** Narrows a wire `source` to the values this build knows. */
function statusSource(value: unknown): ConnectorStatusSource {
  return value === 'identity' || value === 'project' ? value : null;
}

/** The project's merged connector status, or null where the service has no such route yet. */
export async function fetchConnectorStatus(
  projectId: string,
): Promise<QualityConnectorStatusList | null> {
  const wire = await getOptionalJson<ConnectorStatusWire>(
    `/projects/${encodeURIComponent(projectId)}/connector-status`,
    'Failed to load connector status',
  );
  if (!wire) return null;
  return {
    connectors: (wire.connectors ?? [])
      .filter((row) => typeof row.provider === 'string')
      .map((row) => ({
        provider: row.provider as string,
        connected: row.connected === true,
        source: statusSource(row.source),
        ...ownerChoiceFromWire(row),
      })),
    unavailable: wire.unavailable === true,
  };
}

/** The owner-choice fields of one status row, each left out when an older service did not send it. */
function ownerChoiceFromWire(row: {
  chosen_owner_id?: unknown;
  owner_options?: unknown;
}): Pick<QualityConnectorStatus, 'chosenOwnerId' | 'ownerOptions'> {
  const choice: Pick<QualityConnectorStatus, 'chosenOwnerId' | 'ownerOptions'> =
    {};
  if (row.chosen_owner_id !== undefined) {
    choice.chosenOwnerId =
      typeof row.chosen_owner_id === 'string' ? row.chosen_owner_id : null;
  }
  if (Array.isArray(row.owner_options)) {
    choice.ownerOptions = row.owner_options.filter(
      (ownerId): ownerId is string => typeof ownerId === 'string',
    );
  }
  return choice;
}

/** The URL of the caller's owner choice for one connector of a project. */
function connectorChoiceUrl(projectId: string, provider: string): string {
  return `${API_BASE}/projects/${encodeURIComponent(projectId)}/connector-choices/${encodeURIComponent(provider)}`;
}

/** Runs Quality for the caller through `ownerUserId`'s shared connection to `provider`. */
export async function chooseConnectorOwner(
  projectId: string,
  provider: string,
  ownerUserId: string,
): Promise<void> {
  const res = await apiFetch(connectorChoiceUrl(projectId, provider), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ owner_user_id: ownerUserId }),
  });
  if (!res.ok) throw await responseError(res, 'Could not use that connection');
}

/** Stops running Quality through an owner's connection to `provider`. */
export async function clearConnectorOwner(
  projectId: string,
  provider: string,
): Promise<void> {
  const res = await apiFetch(connectorChoiceUrl(projectId, provider), {
    method: 'DELETE',
  });
  if (!res.ok)
    throw await responseError(res, 'Could not stop using that connection');
}

/** Query keys of the setup reads. */
export const qualitySetupKeys = {
  connectorStatus: (projectId: string) =>
    ['quality', 'connector-status', projectId] as const,
  tmsProjects: (projectId: string, provider: TmsProvider) =>
    ['quality', 'tms-projects', projectId, provider] as const,
};

export function connectorStatusQueryOptions(projectId: string | undefined) {
  return queryOptions({
    queryKey: qualitySetupKeys.connectorStatus(projectId ?? ''),
    queryFn: () => fetchConnectorStatus(projectId ?? ''),
    staleTime: 30_000,
    enabled: Boolean(projectId),
    retry: false,
  });
}

export function tmsProjectsQueryOptions(
  projectId: string,
  provider: TmsProvider,
  enabled = true,
) {
  return queryOptions({
    queryKey: qualitySetupKeys.tmsProjects(projectId, provider),
    queryFn: () => fetchTmsProjects(projectId, provider),
    staleTime: 60_000,
    enabled: enabled && projectId.length > 0,
    retry: false,
  });
}
